import { notifications, NOTIFICATIONS } from '@egw/notifications'
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister'
import { QueryClient, QueryClientProvider as QueryClientProviderTanStack } from '@tanstack/react-query'
import { PersistQueryClientProvider as PersistQueryClientProviderTanStack } from '@tanstack/react-query-persist-client'
import { useEffect, type FC, type PropsWithChildren } from 'react'

// Storage persister setup with platform detection
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const createPersister = () => {
  // Use localStorage for web platforms

  return createAsyncStoragePersister({
    key: 'EGW',
    storage: window.sessionStorage,
    throttleTime: 1000,
  })
}

// State management
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
    },
  },
})

// Initialize persister

const persister = createPersister()

export const PersistQueryClientProvider: FC<PropsWithChildren> = ({ children }) => {
  useEffect(() => {
    // Notifications on signOut clear the queryClient and persisted data
    // This is critical for security in financial applications
    const handleSignOut = (): void => {
      queryClient.clear()
    }

    notifications.on(NOTIFICATIONS.AUTH.SIGN_OUT, handleSignOut)

    return () => {
      notifications.remove(NOTIFICATIONS.AUTH.SIGN_OUT, handleSignOut)
    }
  }, [])

  useEffect(() => {
    // Invalidate all queries before page unload to force fresh data on reload
    const handleBeforeUnload = (): void => {
      queryClient.clear()
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [])

  return (
    <PersistQueryClientProviderTanStack
      client={queryClient}
      persistOptions={{
        persister,
      }}
    >
      <QueryClientProviderTanStack client={queryClient}>{children}</QueryClientProviderTanStack>
    </PersistQueryClientProviderTanStack>
  )
}

// Legacy alias for backward compatibility
export const QueryClientProvider = PersistQueryClientProvider

export { useQueryClient, type UseQueryResult } from '@tanstack/react-query'
export * from './account/account'
export * from './chat/chat'
export * from './customer/customer'
export * from './documents/documents'
export * from './investments/investments'
export * from './passkey/passkey'
export * from './product/product'
export * from './transactions/transactions'
